'use client';

import React from 'react';
import { cn, composeTailwindRenderProps, focusRing } from '@utils';
import {
	ListBox as AriaListBox,
	ListBoxItem as AriaListBoxItem,
	Collection,
	composeRenderProps,
	Header,
	Section,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';

import type {
	ListBoxProps as AriaListBoxProps,
	ListBoxItemProps,
	SectionProps,
} from 'react-aria-components';

import { TickQld } from '../icons';
import { iconStyle } from './Field';

export type ListBoxProps<T> = Omit<AriaListBoxProps<T>, 'layout' | 'orientation'>;

export function ListBox<T extends object>({ children, ...props }: ListBoxProps<T>) {
	return (
		<AriaListBox
			{...props}
			className={composeTailwindRenderProps(
				props.className,
				'border-light-border-alt rounded-xs border-2 p-1 outline-0',
			)}
		>
			{children}
		</AriaListBox>
	);
}

export const itemStyles = tv({
	extend: focusRing,
	base: 'group relative flex cursor-default items-center gap-8 rounded-xs px-2.5 py-1.5 text-sm will-change-transform select-none',
	variants: {
		isSelected: {
			false: 'text-light-text-lighter hover:bg-light-background-shade -outline-offset-2',
			true: 'text-light-text -outline-offset-4 outline-white [&+[data-selected]]:rounded-t-none [&:has(+[data-selected])]:rounded-b-none',
		},
		isDisabled: {
			true: 'text-slate-300',
		},
	},
});

export function ListBoxItem(props: ListBoxItemProps) {
	const textValue =
		props.textValue ?? (typeof props.children === 'string' ? props.children : undefined);
	return (
		<AriaListBoxItem {...props} textValue={textValue} className={itemStyles}>
			{composeRenderProps(props.children, children => (
				<>
					{children}
					<div className="bg-neutral-white/20 absolute right-4 bottom-0 left-4 hidden h-px forced-colors:bg-[HighlightText] [.group[data-selected]:has(+[data-selected])_&]:block" />
				</>
			))}
		</AriaListBoxItem>
	);
}

export const dropdownItemStyles = tv({
	base: 'group z-0 flex cursor-pointer items-center gap-4 overflow-hidden rounded-xs px-3 py-2 text-sm outline-0 select-none',
	variants: {
		theme: {
			light: 'text-light-text focus:bg-light-background-shade',
			dark: 'text-dark-text bg-dark-background focus:brightness-115',
		},
		isDisabled: {
			true: 'cursor-default',
		},
	},

	defaultVariants: {
		theme: 'light',
	},
});

export function DropdownItem({
	theme,
	hideIcon,
	...props
}: ListBoxItemProps & { theme?: 'light' | 'dark'; hideIcon?: boolean }) {
	const textValue =
		props.textValue ?? (typeof props.children === 'string' ? props.children : undefined);

	return (
		<AriaListBoxItem
			{...props}
			textValue={textValue}
			className={dropdownItemStyles({ theme, isDisabled: props.isDisabled })}
		>
			{composeRenderProps(props.children, (children, { isSelected, isDisabled }) => (
				<>
					<span
						className={cn(
							'flex flex-1 items-center gap-2 truncate font-normal',
							isSelected && 'font-semibold',
							isDisabled && 'text-light-text-lighter',
						)}
					>
						{children}
					</span>
					{hideIcon && (
						<span className="flex items-center">
							{isSelected && <TickQld className={iconStyle({ theme })} />}
						</span>
					)}
				</>
			))}
		</AriaListBoxItem>
	);
}

export interface DropdownSectionProps<T> extends SectionProps<T> {
	title?: string;
}

export function DropdownSection<T extends object>(props: DropdownSectionProps<T>) {
	return (
		<Section className="after:block after:h-[5px] after:content-[''] first:-mt-[5px]">
			<Header className="sticky -top-[5px] z-10 -mx-1 -mt-px truncate border-y bg-gray-100/60 px-4 py-1 text-sm font-semibold text-gray-500 backdrop-blur-md supports-[-moz-appearance:none]:bg-gray-100 [&+*]:mt-1">
				{props.title}
			</Header>
			<Collection items={props.items}>{props.children}</Collection>
		</Section>
	);
}
